import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'

import type { OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import OfferCalendarSidebarDatePicker from '../Filters/CalendarSidebarPicker/OfferCalendarSidebarDatePicker'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import useDelayedTogglingBoolean from 'hooks/useDelayedTogglingBoolean'
import { useScreenSizeBetween } from 'hooks/useScreenSize'
import { getDurationOptions } from 'selectors/offerPage/offerPageSelectors'
import type { OfferDurationOption } from '../Filters/DurationTabs/OfferDurationTabs'
import { min } from 'lib/array/arrayUtils'
import OfferDurationOptions from '../Filters/Duration/OfferDurationOptions'
import OfferCapacityWithAgesVerticalSidebarSelection from '../Filters/CapacityWithAges/OfferCapacityWithAgesVerticalSidebarSelection'

const calendarFilters: Array<OfferPageFilterType> = ['capacity', 'duration']

interface Props {
  className?: string
  activeFilter?: OfferPageFilterType
  offer: App.VillaOffer
  durationOptions: Array<OfferDurationOption>
}

function VillasPageSidebarFilters(props: Props) {
  const {
    activeFilter,
    durationOptions,
  } = props

  const intl = useIntl()
  const isOnProperScreenSize = useScreenSizeBetween('mobile', 'tablet')
  const dispatch = useOfferPageDispatch()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const closeFilter = useCallback(() => {
    dispatch({ type: OfferPageActions.closeFilter })
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.CloseCross)
  }, [dispatch, onUserAnalyticsAction])

  const backToFilterList = useCallback(() => {
    if (activeFilter === 'duration') {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
    } else if (activeFilter === 'checkin') {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'duration' })
    }
  }, [activeFilter, dispatch])

  const onContinue = useCallback(() => {
    dispatch({ type: OfferPageActions.filterContinue })
  }, [dispatch])

  // Workaround for bug in React Modal when quickly switching between open/closed
  // https://github.com/reactjs/react-modal/issues/888
  // https://github.com/reactjs/react-modal/issues/808
  // TODO: Come up with better solution, because this will always delay opening the modal
  const delayedOpen = useDelayedTogglingBoolean(!!activeFilter, { onDelay: 300 })

  const minimumDuration = useMemo(() => min(durationOptions, option => option.duration)?.duration ?? 1, [durationOptions])

  const durationSubtitle = minimumDuration > 1 ? intl.formatMessage(
    {
      id: 'A6GXplLiD6',
      defaultMessage: 'Minimum stay for this offer is {count, plural, one {# night} other {# nights}}',
      description: 'Subtitle showing minimum stay requirement for villa duration filter',
    },
    { count: minimumDuration },
  ) : undefined

  return (
    <ModalBase
      isOpen={delayedOpen && isOnProperScreenSize}
      onClose={closeFilter}
      mode="drawer"
    >
      {activeFilter === 'capacity' && <OfferCapacityWithAgesVerticalSidebarSelection
        onClose={closeFilter}
        onApply={onContinue}
        disableMultiRoom
        footerText={intl.formatMessage({
          id: 'cylYvgCSQE',
          defaultMessage: 'Continue',
          description: 'Footer button text for villa capacity selection sidebar',
        })}
      />}
      {activeFilter === 'duration' && <>
        <ModalHeader
          title={intl.formatMessage({
            id: '/Wd845oo9M',
            defaultMessage: 'How long do you want to stay?',
            description: 'Title for villa duration filter modal in the search or listing filters panel',
          })}
          subtitle={durationSubtitle}
          onCloseButtonClick={closeFilter}
          onBackButtonClick={backToFilterList}
        />
        <ModalBody>
          <ModalContent>
            <OfferDurationOptions onSelect={onContinue} />
          </ModalContent>
        </ModalBody>
      </>}
      {activeFilter === 'checkin' && <OfferCalendarSidebarDatePicker
        onClose={closeFilter}
        onBack={backToFilterList}
        subFilters={calendarFilters}
      />}
    </ModalBase>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  activeFilter: state.activeFilter,
  offer: state.offer,
  durationOptions: getDurationOptions(state, appState),
}), VillasPageSidebarFilters)
